// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * In-headset Browse — the Browse page of the side panel (vrSidePanel).
 *
 * AYNI (2026-10-04, the AR refresh): the first VR browse panel was a list
 * of about six rows under one row of category chips, and chips that did
 * not fit were dropped, so some categories could not be reached; it had
 * no search. This page follows the browser's Browse instead:
 *   - Catalog and Recent tabs;
 *   - the browser's kind chips (Live, Forecast, …) and theme chips,
 *     flowed onto as many rows as they need;
 *   - search, ranked by the browser's own smart search (the host's
 *     `search`). AYNI (owner on Quest, 2026-11-04): typed on the pop-up
 *     keyboard (`vrKeyboard`), which stands on its own in front of the
 *     viewer; this page keeps its chips and cards in view meanwhile and
 *     narrows them with every key;
 *   - cards three across with the thumbnail, the kind and a lock on
 *     streams outside the plan, in one list that scrolls: the thumbstick
 *     while the ray is on it, a drag with the trigger, or its scroll bar
 *     (owner, 2026-10-04: arrows were too slow for the whole catalogue).
 *     Only the rows in view are drawn.
 *
 * Pure parts (`browseResults`, `reduceBrowse`, `browseChips`) hold the
 * rules and are tested; `drawBrowse` only paints them.
 */

import { t } from '../i18n'
import type { VrDatasetEntry } from './vrSession'
import type { VrPanelAction } from './vrSidePanel'
import { PANEL_COLORS, SCROLLBAR_W, clampScroll, type PanelPainter, type ScrollRegion } from './vrPanelKit'

export interface VrBrowseView {
  tab: 'catalog' | 'recent'
  /** A DataKind id, or null for every kind. */
  kind: string | null
  /** A Theme id, or null for every theme. */
  theme: string | null
  query: string
  /** How far the cards are scrolled, in canvas pixels. */
  scroll: number
  /** The search's pop-up keyboard is open. */
  keyboard: boolean
}

export function initialBrowseView(): VrBrowseView {
  return { tab: 'catalog', kind: null, theme: null, query: '', scroll: 0, keyboard: false }
}

export interface VrBrowseResults {
  items: VrDatasetEntry[]
  total: number
}

const MAX_QUERY = 40

function plainLower(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/**
 * The cards a view shows: Recent in the order opened; the catalogue
 * filtered by kind and theme and, with a query, ranked by `search` (or
 * matched on title words where there is none).
 */
export function browseResults(
  entries: readonly VrDatasetEntry[],
  view: VrBrowseView,
  recentIds: readonly string[],
  search?: (query: string) => string[] | null,
  bodies: readonly string[] | null = null,
): VrBrowseResults {
  let list: VrDatasetEntry[]
  if (view.tab === 'recent') {
    const byId = new Map(entries.map((e) => [e.id, e]))
    list = recentIds.map((id) => byId.get(id)).filter((e): e is VrDatasetEntry => !!e)
  } else {
    // AYNI (owner on Quest, 2026-11-05): a visit in Solar System mode keeps the catalogue to that body, as in the browser.
    list = entries.filter((e) =>
      (bodies === null || bodies.includes(e.body ?? 'earth')) &&
      (view.kind === null || e.kind === view.kind) &&
      (view.theme === null || (e.themes ?? []).some((th) => th.id === view.theme)))
    const q = view.query.trim()
    if (q) {
      const ranked = search?.(q) ?? null
      if (ranked) {
        const rank = new Map(ranked.map((id, i) => [id, i]))
        list = list.filter((e) => rank.has(e.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!)
      } else {
        const words = plainLower(q).split(/\s+/).filter(Boolean)
        list = list.filter((e) => {
          const title = plainLower(e.title)
          return words.every((w) => title.includes(w))
        })
      }
    }
  }
  return { items: list, total: list.length }
}

/** The view after a press that belongs to the panel itself. */
export function reduceBrowse(view: VrBrowseView, action: VrPanelAction): VrBrowseView {
  switch (action.kind) {
    case 'browse-tab':
      return { ...view, tab: action.tab, scroll: 0, keyboard: action.tab === 'recent' ? false : view.keyboard }
    case 'browse-kind':
      return { ...view, kind: action.value, scroll: 0 }
    case 'browse-theme':
      return { ...view, theme: action.value, scroll: 0 }
    case 'browse-search':
      return { ...view, tab: 'catalog', keyboard: !view.keyboard, scroll: 0 }
    case 'key': {
      if (action.key === 'done') return { ...view, keyboard: false }
      if (action.key === 'clear') return { ...view, query: '', scroll: 0 }
      if (action.key === 'back') return { ...view, query: view.query.slice(0, -1), scroll: 0 }
      if (view.query.length >= MAX_QUERY) return view
      const ch = action.key === 'space' ? ' ' : action.key
      if (ch === ' ' && (view.query === '' || view.query.endsWith(' '))) return view
      return { ...view, query: view.query + ch, scroll: 0 }
    }
    default:
      return view
  }
}

/** The kind and theme chips for a catalogue: only those some dataset has, in the browser's order. */
export function browseChips(entries: readonly VrDatasetEntry[]): {
  kinds: Array<{ id: string; label: string }>
  themes: Array<{ id: string; label: string }>
} {
  const kinds = new Map<string, string>()
  const themes = new Map<string, string>()
  for (const e of entries) {
    if (e.kind && e.kindLabel && !kinds.has(e.kind)) kinds.set(e.kind, e.kindLabel)
    for (const th of e.themes ?? []) if (!themes.has(th.id)) themes.set(th.id, th.label)
  }
  const order = (e: VrDatasetEntry) => e.kindOrder ?? 0
  const kindOrder = new Map<string, number>()
  for (const e of entries) if (e.kind && !kindOrder.has(e.kind)) kindOrder.set(e.kind, order(e))
  const themeOrder = new Map<string, number>()
  for (const e of entries) for (const th of e.themes ?? []) if (!themeOrder.has(th.id)) themeOrder.set(th.id, th.order ?? 0)
  return {
    kinds: [...kinds].sort((a, b) => (kindOrder.get(a[0]) ?? 0) - (kindOrder.get(b[0]) ?? 0)).map(([id, label]) => ({ id, label })),
    themes: [...themes].sort((a, b) => (themeOrder.get(a[0]) ?? 0) - (themeOrder.get(b[0]) ?? 0)).map(([id, label]) => ({ id, label })),
  }
}

const PAD = 40
const W = 1120
const H = 1000

/** What a Browse redraw leaves behind: the list's scroll region and the cards it drew. */
export interface BrowseDrawn {
  region: ScrollRegion
  visible: VrDatasetEntry[]
}

/** Card geometry: three across, rows of a fixed height. */
const COLS = 3
const CARD_GAP = 20
const CARD_H = 250

/** Draw the Browse page; the cards scroll by `view.scroll` (clamped here). */
export function drawBrowse(
  p: PanelPainter<VrPanelAction>,
  view: VrBrowseView,
  entries: readonly VrDatasetEntry[],
  results: VrBrowseResults,
  currentId: string | null,
  thumbs: ReadonlyMap<string, HTMLImageElement>,
  /** The body Browse keeps to (its chip's name), or null. */
  bodyLabel: string | null = null,
): BrowseDrawn {
  p.frame(32)

  // --- Header: title, tabs, close.
  p.text(t('vr.browse.title'), PAD, 70, { size: 42, weight: 700 })
  const tabX = PAD + p.measure(t('vr.browse.title'), 42, 700) + 36
  const tabW = 190
  p.box(tabX, 36, tabW * 2 + 12, 68, 18, 'rgba(255,255,255,0.06)')
  p.button(tabX + 4, 40, tabW, 60, t('browse.tab.catalog'), { kind: 'browse-tab', tab: 'catalog' }, { primary: view.tab === 'catalog', size: 26 })
  p.button(tabX + 8 + tabW, 40, tabW, 60, t('browse.tab.recent'), { kind: 'browse-tab', tab: 'recent' }, { primary: view.tab === 'recent', size: 26 })
  p.button(W - PAD - 68, 36, 68, 68, '', { kind: 'close' }, { icon: 'close', radius: 34 })

  // --- Search field: a press opens the pop-up keyboard (a second one closes it).
  let y = 124
  if (view.tab === 'catalog') {
    const fieldW = W - PAD * 2
    p.box(PAD, y, fieldW, 72, 18, view.keyboard ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.07)', view.keyboard ? PANEL_COLORS.accent : PANEL_COLORS.border, 2)
    p.icon('search', PAD + 40, y + 36, 30, PANEL_COLORS.textSecondary)
    const hasQuery = view.query.length > 0
    p.text(hasQuery ? `${view.query}${view.keyboard ? '|' : ''}` : t('browse.search.placeholder'), PAD + 76, y + 37, {
      size: 30,
      color: hasQuery ? PANEL_COLORS.text : PANEL_COLORS.muted,
      maxWidth: fieldW - 200,
    })
    p.hit(PAD, y, fieldW - (hasQuery ? 100 : 0), 72, { kind: 'browse-search' })
    if (hasQuery) p.button(W - PAD - 92, y + 8, 84, 56, '', { kind: 'key', key: 'clear' }, { icon: 'close', radius: 14 })
    y += 92
  }

  // --- The chips (the keys are on the pop-up keyboard).
  if (view.tab === 'catalog') {
    const { kinds, themes } = browseChips(entries)
    // Sized so the kinds fit one row and the themes two, leaving the cards room.
    const rowH = 46
    const gap = 10
    const chipPad = 15
    let x = PAD
    let rows = 1
    const place = (label: string, action: VrPanelAction, active: boolean): void => {
      const w = p.measure(label, 22, 600) + chipPad * 2
      if (x + w > W - PAD) {
        if (rows >= 4) return
        rows++
        x = PAD
        y += rowH + gap
      }
      p.chip(x, y, label, action, active, 22, rowH, chipPad)
      x += w + gap
    }
    // The body chip first, as the browser shows it: its name and a ✕ that shows every body again.
    if (bodyLabel) place(`${bodyLabel}  ✕`, { kind: 'browse-body-clear' }, true)
    place(t('vr.browse.allKinds'), { kind: 'browse-kind', value: null }, view.kind === null)
    for (const k of kinds) place(k.label, { kind: 'browse-kind', value: k.id }, view.kind === k.id)
    // Themes start a row of their own so the two kinds of chip read as two groups.
    x = W
    place(t('vr.browse.allThemes'), { kind: 'browse-theme', value: null }, view.theme === null)
    for (const th of themes) place(th.label, { kind: 'browse-theme', value: th.id }, view.theme === th.id)
    y += rowH + 24
  } else {
    y += 8
  }

  // --- Cards, in a list that scrolls.
  const footerTop = H - 80
  const region: ScrollRegion = { x: PAD, y, w: W - PAD * 2, h: footerTop - 12 - y, content: 0 }
  const listW = region.w - SCROLLBAR_W - 16
  const cardW = (listW - CARD_GAP * (COLS - 1)) / COLS
  const rows = Math.ceil(results.items.length / COLS)
  region.content = rows > 0 ? rows * (CARD_H + CARD_GAP) - CARD_GAP : 0
  const scroll = clampScroll(view.scroll, region)
  const visible: VrDatasetEntry[] = []
  if (results.total === 0) {
    const msg = view.tab === 'recent'
      ? t('vr.browse.noRecent')
      : view.query
        ? t('vr.browse.noMatch', { query: view.query })
        : t('vr.browse.empty')
    p.text(msg, W / 2, y + 120, { size: 28, color: PANEL_COLORS.muted, align: 'center', maxWidth: W - PAD * 2 })
  }
  p.clip(region.x, region.y, region.w, region.h)
  const firstRow = Math.max(0, Math.floor(scroll / (CARD_H + CARD_GAP)))
  const lastRow = Math.min(rows - 1, Math.floor((scroll + region.h) / (CARD_H + CARD_GAP)))
  for (let row = firstRow; row <= lastRow; row++) {
    for (let col = 0; col < COLS; col++) {
      const d = results.items[row * COLS + col]
      if (!d) continue
      visible.push(d)
      const x = PAD + col * (cardW + CARD_GAP)
      const cy = region.y + row * (CARD_H + CARD_GAP) - scroll
      drawCard(p, d, x, cy, cardW, CARD_H, d.id === currentId, thumbs)
      // Only the part of a card inside the viewport can be pressed.
      const top = Math.max(cy, region.y)
      const bottom = Math.min(cy + CARD_H, region.y + region.h)
      if (bottom > top) p.hit(x, top, cardW, bottom - top, { kind: 'browse-select', id: d.id, locked: d.locked ?? null })
    }
  }
  p.unclip()
  p.scrollbar(region, scroll)

  // --- Footer: how many, and how to move through them.
  if (results.total > 0) {
    p.text(t('vr.browse.count', { count: results.total }), PAD, footerTop + 26, { size: 26, color: PANEL_COLORS.textSecondary })
    p.text(t('vr.browse.scrollHint'), W - PAD, footerTop + 26, { size: 22, color: PANEL_COLORS.muted, align: 'right' })
  }
  return { region, visible }
}

function drawCard(
  p: PanelPainter<VrPanelAction>,
  d: VrDatasetEntry,
  x: number,
  cy: number,
  cardW: number,
  cardH: number,
  showing: boolean,
  thumbs: ReadonlyMap<string, HTMLImageElement>,
): void {
  p.box(x, cy, cardW, cardH, 18, showing ? 'rgba(34,197,94,0.1)' : PANEL_COLORS.surface, showing ? PANEL_COLORS.accent : PANEL_COLORS.border, showing ? 3 : 1.5)
  // Thumbnail across the top; under it room for two lines of title and the badge.
  const thumbH = Math.max(56, cardH - 128)
  p.g.save()
  p.roundPath(x + 8, cy + 8, cardW - 16, thumbH, 12)
  p.g.clip()
  p.g.fillStyle = 'rgba(255,255,255,0.05)'
  p.g.fillRect(x + 8, cy + 8, cardW - 16, thumbH)
  const img = d.thumbnailUrl ? thumbs.get(d.thumbnailUrl) : undefined
  if (img && img.complete && img.naturalWidth > 0) {
    const scale = Math.max((cardW - 16) / img.naturalWidth, thumbH / img.naturalHeight)
    const dw = img.naturalWidth * scale
    const dh = img.naturalHeight * scale
    try {
      p.g.drawImage(img, x + 8 + (cardW - 16 - dw) / 2, cy + 8 + (thumbH - dh) / 2, dw, dh)
    } catch { /* a tainted or broken image keeps the placeholder */ }
  }
  if (d.locked) {
    p.g.fillStyle = 'rgba(13,13,18,0.45)'
    p.g.fillRect(x + 8, cy + 8, cardW - 16, thumbH)
  }
  p.g.restore()
  if (d.locked) {
    p.box(x + cardW - 62, cy + 18, 44, 44, 22, 'rgba(13,13,18,0.85)')
    p.icon('lock', x + cardW - 40, cy + 40, 24, PANEL_COLORS.text)
  }
  const lines = p.wrap(d.title, cardW - 28, 2, 23, 650)
  lines.forEach((line, li) => p.text(line, x + 14, cy + 8 + thumbH + 26 + li * 28, { size: 23, weight: 650, color: d.locked ? PANEL_COLORS.textSecondary : PANEL_COLORS.text }))
  const badge = d.locked ?? (showing ? t('vr.browse.showing') : d.kindLabel ?? '')
  if (badge) {
    const bw = Math.min(cardW - 28, p.measure(badge, 20, 700) + 24)
    const live = !d.locked && !showing && d.kind === 'real-time'
    p.box(x + 14, cy + cardH - 40, bw, 30, 15, live ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.08)')
    p.text(badge, x + 26, cy + cardH - 25, { size: 20, weight: 700, color: live ? PANEL_COLORS.accentText : PANEL_COLORS.textSecondary, maxWidth: bw - 20 })
  }
}
